import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adApi } from '../../../api/ad.api';
import { CreateDeploymentMarkerRequest } from '../../../types/Ad.api.type';
import { Button } from '../../ui/button';
import toast from 'react-hot-toast';

const PLATFORMS = ['BE', 'TOSS', 'WEB', 'ALL'] as const;

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function daysAgoIso(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** G1-2 섹션 3: 배포 타임라인 마커 + 입력 폼 */
const DeploymentTimeline = () => {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<CreateDeploymentMarkerRequest>({
    markerDate: todayIso(),
    version: '',
    platform: 'BE',
    description: '',
  });

  const { data: markers, isLoading } = useQuery({
    queryKey: ['deploymentMarkers', 30],
    queryFn: () => adApi.getDeploymentMarkers(daysAgoIso(30), todayIso()).then((res) => res.data),
    staleTime: 1000 * 60 * 60,
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateDeploymentMarkerRequest) => adApi.createDeploymentMarker(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deploymentMarkers'] });
      toast.success('배포 마커가 등록되었어요.');
      setShowForm(false);
      setForm({ markerDate: todayIso(), version: '', platform: 'BE', description: '' });
    },
    onError: () => toast.error('등록에 실패했어요.'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => adApi.deleteDeploymentMarker(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deploymentMarkers'] });
      toast.success('삭제되었어요.');
    },
    onError: () => toast.error('삭제에 실패했어요.'),
  });

  const handleSubmit = () => {
    if (!form.version.trim()) {
      toast.error('버전을 입력하세요.');
      return;
    }
    createMutation.mutate(form);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold text-gray-700">3. 배포 타임라인 (최근 30일)</h4>
        <Button size="sm" variant="outline" onClick={() => setShowForm(!showForm)}>
          {showForm ? '취소' : '+ 마커 추가'}
        </Button>
      </div>

      {showForm && (
        <div className="mb-3 p-3 border rounded bg-gray-50 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-gray-500 block mb-1">배포 날짜</label>
              <input
                type="date"
                value={form.markerDate}
                onChange={(e) => setForm({ ...form, markerDate: e.target.value })}
                className="w-full border rounded px-2 py-1 text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">버전</label>
              <input
                type="text"
                value={form.version}
                onChange={(e) => setForm({ ...form, version: e.target.value })}
                placeholder="v1.9.2"
                className="w-full border rounded px-2 py-1 text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">플랫폼</label>
              <select
                value={form.platform}
                onChange={(e) => setForm({ ...form, platform: e.target.value })}
                className="w-full border rounded px-2 py-1 text-sm"
              >
                {PLATFORMS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">설명</label>
              <input
                type="text"
                value={form.description ?? ''}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="예: S11-6 배포"
                className="w-full border rounded px-2 py-1 text-sm"
              />
            </div>
          </div>
          <Button size="sm" onClick={handleSubmit} disabled={createMutation.isPending}>
            등록
          </Button>
        </div>
      )}

      {isLoading ? (
        <div className="text-xs text-gray-400">로딩 중...</div>
      ) : !markers || markers.length === 0 ? (
        <div className="text-xs text-gray-400 italic">아직 마커가 없습니다.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs border-collapse">
            <thead className="bg-gray-50">
              <tr>
                <th className="border px-2 py-1 text-left">날짜</th>
                <th className="border px-2 py-1 text-left">버전</th>
                <th className="border px-2 py-1 text-left">플랫폼</th>
                <th className="border px-2 py-1 text-left">설명</th>
                <th className="border px-2 py-1 text-center"></th>
              </tr>
            </thead>
            <tbody>
              {markers.map((m) => (
                <tr key={m.id} className="hover:bg-gray-50">
                  <td className="border px-2 py-1 font-mono">{m.markerDate}</td>
                  <td className="border px-2 py-1 font-medium">{m.version}</td>
                  <td className="border px-2 py-1">{m.platform}</td>
                  <td className="border px-2 py-1 text-gray-600">{m.description ?? '—'}</td>
                  <td className="border px-2 py-1 text-center">
                    <button
                      className="text-xs text-red-500 hover:underline"
                      onClick={() => {
                        if (window.confirm(`${m.version} 마커를 삭제할까요?`)) {
                          deleteMutation.mutate(m.id);
                        }
                      }}
                    >
                      삭제
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DeploymentTimeline;
